import { FC, useEffect, useState } from 'react'

import { inflect } from 'inflection'
import { match } from 'ts-pattern'
import { Currency, useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import {
  Text,
  GreyWhite,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useCustomerPaginatedFetcher } from 'Customer/hooks/useCustomerPaginatedFetcher'
import {
  ALL_BILLING_FREQUENCIES,
  PRICING_MODEL_LABELS,
  toBillingType,
  toCurrencySymbol,
  usePrevious,
  type BillingType
} from '@sequencehq/utils'
import { formatDuration } from '@sequencehq/utils/dates'

// Imported directly from the types module (rather than the `conditions`
// barrel) to avoid a circular import: the barrel re-exports
// `getUpstreamConditionalProperties`, which transitively pulls the node
// definitions registry — including the product property editor — back
// into the cycle.
import {
  ConditionalPropertyValueFormatter,
  ConditionalValueFormatter,
  OperatorFormatter,
  PERCENTAGE_UNIT
} from 'Automations/nodeDefinitions/conditions/conditions.types'
import type { PricingModel } from 'common/drawers/PricingEditor/domain/pricingEditor.domain.types'
import {
  DEFAULT_QUOTE_COMPARISON_LABELS,
  PLURAL_QUOTE_COMPARISON_LABELS,
  QUOTE_PRICING_COMPARISON_DISPLAY,
  readPricingComparison,
  type QuoteComparisonLabels
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/quoteComparison'
import {
  hasPhaseDuration,
  readPhaseDurationValue,
  readPhaseNameValue
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'
import { useQuoteTemplateOptions } from './components/QuoteTemplateSelector'
import { useAllUsers } from 'common/hooks/useUsers'
import { dequal } from 'dequal'
import { QUOTE_DEAL_TYPE_LABELS } from '@sequencehq/quote-content'
import { phaseDurationToInput } from 'Automations/nodeDefinitions/definitions/start/startQuoteRequiresApproval/conditionalProperties/phase/phaseDurationInput'

const PRODUCT_DISCOUNT_SUB_OPERATOR_LABELS: Record<string, string> = {
  EQUALS: 'Equals',
  LESS_THAN: 'Less than',
  MORE_THAN: 'More than'
}

const CUSTOM_PROPERTY_SUB_OPERATOR_LABELS: Record<string, string> = {
  EQUALS: 'equals',
  NOT_EQUALS: 'does not equal'
}

/**
 * TODO
 * We can probably better co-locate these with the relevant inputs/conditions so that it's
 * a bit easier to view everything within one file for a particular input, e.g.
 * the product value input.
 */

/**
 * Formats an amount-with-unit value (`{ amount, unit }`) for label rendering.
 * PERCENTAGE_UNIT shows `25%`; a currency code shows `$1,234.56` using the
 * unit's symbol; an unspecified unit falls back to the locale-formatted number.
 */
const formatAmountByUnit = (amount: unknown, unit: unknown): string => {
  if (unit === PERCENTAGE_UNIT) return `${amount}%`
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2
  })
  if (!unit) return formatted
  return `${toCurrencySymbol(unit as Currency)}${formatted}`
}

export const formatAmountWithUnit: ConditionalValueFormatter = ({ value }) =>
  formatAmountByUnit(value?.amount ?? value, value?.unit)

export const formatPercentage: ConditionalValueFormatter = ({ value }) =>
  `${value}%`

export const formatText: ConditionalValueFormatter = ({ value }) => `"${value}"`

export const formatProductCount: ConditionalValueFormatter = ({ value }) => (
  <ProductCountWithTooltip ids={Array.isArray(value) ? value : []} />
)

export const formatProductPricingModel: ConditionalValueFormatter = ({
  value
}) => {
  const pricingModel = value?.pricingModel as PricingModel | undefined
  const label = pricingModel ? PRICING_MODEL_LABELS[pricingModel] : undefined
  if (!label) return ''
  // The sub-operator (IS / IS_NOT) is stored on `value.operator`; render it
  // before the model label so the canvas reads as "Pricing model is LINEAR"
  // or "Pricing model is not LINEAR".
  const subOperatorLabel = value?.operator === 'IS_NOT' ? 'is not' : 'is'
  return (
    <>
      {subOperatorLabel}{' '}
      <Box as="span" fontWeight={600}>
        {label}
      </Box>
    </>
  )
}

export const formatProductBillingFrequency: ConditionalValueFormatter = ({
  value
}) => {
  // Read the label off the same list the picker offers, so the canvas can't
  // word a frequency differently from the row that set it.
  const label = ALL_BILLING_FREQUENCIES.find(
    frequency => frequency.value === value?.billingFrequency
  )?.label
  if (!label) return ''
  // The sub-operator (IS / IS_NOT) is stored on `value.operator`; render it
  // before the frequency label so the canvas reads as "Billing frequency is
  // Monthly" or "Billing frequency is not Monthly".
  const subOperatorLabel = match(value?.operator)
    .with('IS_NOT', () => 'is not')
    .otherwise(() => 'is')
  return (
    <>
      {subOperatorLabel}{' '}
      <Box as="span" fontWeight={600}>
        {label}
      </Box>
    </>
  )
}

export const formatProductBillingType: ConditionalValueFormatter = ({
  value
}) => {
  const billingType = value?.billingType as BillingType | undefined
  if (!billingType) return ''
  // "Is" is the only comparison, and the expression ignores whatever the row
  // stores for the operator, so the clause is always worded the same way. It
  // used to read the operator and render nothing when it wasn't "is", which
  // left a row that had picked one up evaluating with no label to show for it.
  return (
    <>
      is{' '}
      <Box as="span" fontWeight={600}>
        {toBillingType(billingType)}
      </Box>
    </>
  )
}

/**
 * Renders the chosen template's title before the operator clause, so the
 * canvas reads as "Quote template <title> attachments are equal".
 */
export const formatQuoteTemplateName: ConditionalPropertyValueFormatter = ({
  value
}) => <QuoteTemplateName templateId={value?.templateId} />

const QuoteTemplateName: FC<{ templateId: string | undefined }> = ({
  templateId
}) => {
  const { titlesById } = useQuoteTemplateOptions()
  if (!templateId) return null
  return <>{titlesById.get(templateId) ?? '...'}</>
}

/**
 * Renders just the comparison, lowercased — the operator's own label supplies
 * the template field, so the canvas reads "<template> attachments are equal".
 * Labels are per-field so the phrasing agrees with the subject's number.
 */
export const formatQuoteComparison =
  (
    labels: QuoteComparisonLabels = DEFAULT_QUOTE_COMPARISON_LABELS
  ): ConditionalValueFormatter =>
  ({ value }) =>
    match(value?.operator)
      .with('IS_EQUAL', () => labels.isEqual.toLowerCase())
      .with('IS_NOT_EQUAL', () => labels.isNotEqual.toLowerCase())
      .otherwise(() => '')

/**
 * The subject a pricing row's clause is built around — the comparison, not the
 * field, so the canvas reads "<template> prices are equal" rather than naming
 * pricing twice.
 */
export const formatQuotePricingSubject: OperatorFormatter = ({ value }) =>
  QUOTE_PRICING_COMPARISON_DISPLAY[readPricingComparison(value)].subject

/**
 * The comparison for a pricing row, agreeing with the number of the chosen
 * comparison rather than a phrasing fixed per field.
 */
export const formatQuotePricingSubOperator: ConditionalValueFormatter = ({
  value
}) => {
  const { isPlural } =
    QUOTE_PRICING_COMPARISON_DISPLAY[readPricingComparison(value)]

  return formatQuoteComparison(
    isPlural ? PLURAL_QUOTE_COMPARISON_LABELS : DEFAULT_QUOTE_COMPARISON_LABELS
  )({ value })
}

export const formatProductDiscount: ConditionalValueFormatter = ({ value }) => {
  if (!value?.operator) {
    return ''
  }

  const operatorLabel = (
    PRODUCT_DISCOUNT_SUB_OPERATOR_LABELS[value?.operator] ?? ''
  ).toLowerCase()
  const amountLabel =
    typeof value.amount !== 'undefined'
      ? formatAmountByUnit(value?.amount, value?.unit)
      : ''
  return (
    <>
      <Box as="span" fontWeight={600}>
        {operatorLabel}
      </Box>{' '}
      {amountLabel}
    </>
  )
}

/**
 * Formats a custom-property condition value (`{ operator, value }`) as the
 * comparison clause — e.g. `equals "EU"`. The custom-property key is the
 * operator itself, so it's rendered by the operator label; this formatter only
 * carries the equals/not-equals comparison and the value.
 */
export const formatCustomProperty: ConditionalValueFormatter = ({ value }) => {
  const operatorLabel =
    CUSTOM_PROPERTY_SUB_OPERATOR_LABELS[value?.operator] ??
    CUSTOM_PROPERTY_SUB_OPERATOR_LABELS.EQUALS
  return `${operatorLabel} "${value?.value ?? ''}"`
}

/**
 * Property-level formatter for the PRODUCT property. Resolves the product
 * ID stored on the condition value to the product's name (with a graceful
 * fallback while the product list is still loading). Rendered between the
 * property label and the operator section so each operator's own
 * `formatValue` stays focused on the comparison.
 */
export const formatProductName: ConditionalPropertyValueFormatter = ({
  value
}) => <ProductName productId={value?.productId} />

const ProductName: FC<{ productId: string | undefined }> = ({ productId }) => {
  const { data } = useLoadAllItemsQuery(dashboardv20240509Client.getProducts, {
    limit: 100
  })
  if (!productId) return null
  const productsById = new Map(
    (data?.pages?.flatMap(page => page.items) ?? []).map(p => [p.id, p.name])
  )
  return <>{productsById.get(productId) ?? '...'}</>
}

export const formatUserCount: ConditionalValueFormatter = ({ value }) => (
  <UserCountWithTooltip ids={Array.isArray(value) ? value : []} />
)

export const formatCustomerCount: ConditionalValueFormatter = ({ value }) => (
  <CustomerCountWithTooltip ids={Array.isArray(value) ? value : []} />
)

export const formatCurrencyCode: ConditionalValueFormatter = ({ value }) =>
  String(value)

/**
 * Formats a quote deal type enum value for the condition label, falling back
 * to the raw value for anything outside the known enum.
 */
export const formatDealType: ConditionalValueFormatter = ({ value }) =>
  QUOTE_DEAL_TYPE_LABELS[value as keyof typeof QUOTE_DEAL_TYPE_LABELS] ??
  String(value)

/**
 * Formats a `{ years, months, days }` duration value for the condition label.
 * Comma-delimited (date-fns' default is a single space) and falls back to
 * "0 days" when every component is zero so the label always communicates the
 * unit. Returns a primitive string so the execution-summary group label
 * (which coerces with `String()`) stays readable.
 */
export const formatConditionDuration: ConditionalValueFormatter = ({ value }) =>
  formatDuration(value, { delimiter: ', ', fallback: '0 days' })

export const formatPhaseName: ConditionalValueFormatter = ({ value }) => {
  const { operator, name } = readPhaseNameValue(value)
  if (!operator || operator === 'NOT_SET' || !name) return ''
  return name
}

export const formatPhaseDuration: ConditionalValueFormatter = ({ value }) => {
  const { operator, duration } = readPhaseDurationValue(value)
  if (
    !operator ||
    operator === 'IS_OPEN_ENDED' ||
    !duration ||
    !hasPhaseDuration(duration)
  ) {
    return ''
  }
  return formatConditionDuration({ value: phaseDurationToInput(duration) })
}

/**
 * Formats a payment terms day count for the condition label, e.g. "30 days".
 * Returns a primitive string so the execution-summary group label (which
 * coerces with `String()`) stays readable.
 */
export const formatPaymentTerms: ConditionalValueFormatter = ({ value }) => {
  const days = Number(value)
  if (!Number.isFinite(days)) return ''
  return `${days} ${inflect('day', days)}`
}

const ProductCountWithTooltip: FC<{ ids: string[] }> = ({ ids }) => {
  const { data } = useLoadAllItemsQuery(dashboardv20240509Client.getProducts, {
    limit: 100
  })

  const productsById = new Map(
    (data?.pages?.flatMap(page => page.items) ?? []).map(p => [p.id, p.name])
  )

  const names = ids.map(id => productsById.get(id) ?? id)
  const label = `${ids.length} ${inflect('product', ids.length)}`

  return <CountWithNamesTooltip label={label} names={names} />
}

const UserCountWithTooltip: FC<{ ids: string[] }> = ({ ids }) => {
  const { users } = useAllUsers()

  const usersById = new Map(users.map(u => [u.id, u.label]))

  const names = ids.map(id => usersById.get(id) ?? id)
  const label = `${ids.length} ${inflect('user', ids.length)}`

  return <CountWithNamesTooltip label={label} names={names} />
}

const CustomerCountWithTooltip: FC<{ ids: string[] }> = ({ ids }) => {
  // Resolve only the selected customers by id (search-driven, like the picker)
  // rather than loading every customer just to label the canvas preview.
  const { resolveValues } = useCustomerPaginatedFetcher()
  const [namesById, setNamesById] = useState<Record<string, string>>({})
  const prevIds = usePrevious(ids)

  useEffect(() => {
    /**
     * Ensure we only run the fetch when the ids have changed.
     */
    if (dequal(ids, prevIds)) {
      return
    }

    if (ids.length === 0) {
      setNamesById({})
      return
    }

    resolveValues(ids).then(options => {
      setNamesById(prev => ({
        ...prev,
        ...Object.fromEntries(options.map(o => [o.value, String(o.label)]))
      }))
    })
  }, [resolveValues, ids, prevIds])

  /**
   * If we've not loaded the customer names yet, don't display the
   * tooltip (as it would contain a list of raw uuids). If we fail
   * to resolve one of the names, show a fallback message.
   */
  const names = Object.keys(namesById).length
    ? ids.map(id => namesById[id] ?? '[Could not load customer]')
    : []
  const label = `${ids.length} ${inflect('customer', ids.length)}`

  return <CountWithNamesTooltip label={label} names={names} />
}

const CountWithNamesTooltip: FC<{ label: string; names: string[] }> = ({
  label,
  names
}) => {
  const visibleNames = names.filter(Boolean)
  const isDisabled = visibleNames.length === 0

  if (isDisabled) return <>{label}</>

  return (
    <Tooltip
      label={
        <Flex flexDir="column" gap="2px">
          {visibleNames.map((name, index) => (
            <Text key={index} color={GreyWhite}>
              {name}
            </Text>
          ))}
        </Flex>
      }
    >
      <Text as="span" textDecoration="underline" textDecorationStyle="dotted">
        {label}
      </Text>
    </Tooltip>
  )
}
